/**
 * Published services, shared across the site.
 *
 * The header dropdown, footer, homepage grid, services index and quote form
 * all need the same list. Fetching once at the top of the tree keeps them
 * consistent with each other and avoids five requests for one small payload.
 *
 * Seeded from the bundled fallback so the navigation renders on first paint
 * rather than appearing a moment later — a header whose menu pops in is worse
 * than one that is briefly a version behind.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { serviceService } from "../services/serviceService";

const ServicesContext = createContext({ services: [], nav: [], loading: true });

export function ServicesProvider({ children }) {
  const [services, setServices] = useState([]);
  const [nav, setNav] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    Promise.all([serviceService.list(), serviceService.nav()])
      .then(([list, grouped]) => {
        if (!active) return;
        setServices(Array.isArray(list) ? list : []);
        setNav(Array.isArray(grouped) ? grouped : []);
      })
      .catch(() => {
        // withFallback already degrades to bundled data; nothing further to do.
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(() => ({ services, nav, loading }), [services, nav, loading]);

  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
}

export function useServices() {
  return useContext(ServicesContext);
}
